import { cn } from "@/lib/utils";
import type {
  ActionGroup as ActionGroupType,
  Player,
  ActionType,
  ActionResult,
  RerollPreference,
  SkillKey,
} from "../types";
import { ActionItem } from "./ActionItem";
import { useLanguage } from "../i18n";

/**
 * Get a short label for player skills to display in dropdown
 */
function getPlayerSkillsLabel(player: Player, skillLabels: Record<SkillKey, string>): string {
  const activeSkills = (Object.entries(player.skills) as [SkillKey, boolean][])
    .filter(([_, enabled]) => enabled)
    .map(([skill]) => skillLabels[skill]);

  if (activeSkills.length === 0) {
    return "";
  }

  // Abbreviate skill names for compact display
  const abbreviations: Record<string, string> = {
    "Dodge": "D",
    "Esquive": "E",
    "Sure Hands": "SH",
    "Prise Sûre": "PS",
    "Pass": "P",
    "Passe": "P",
    "Catch": "C",
    "Réception": "R",
    "Sure Feet": "SF",
    "Pieds Agiles": "PA",
    "Block": "B",
  };

  const abbreviated = activeSkills.map(skill => abbreviations[skill] || skill.charAt(0));
  return abbreviated.join(", ");
}

export interface ActionGroupProps {
  group: ActionGroupType;
  players: Record<string, Player>;
  onSetPlayer: (playerId: string | null) => void;
  onAddAction: () => void;
  onRemoveAction: (actionId: string) => void;
  onUpdateActionType: (actionId: string, type: ActionType) => void;
  onUpdateActionResult: (actionId: string, result: ActionResult) => void;
  onUpdateActionReroll: (actionId: string, reroll: RerollPreference) => void;
  onRemove: () => void;
  className?: string;
}

export function ActionGroup({
  group,
  players,
  onSetPlayer,
  onAddAction,
  onRemoveAction,
  onUpdateActionType,
  onUpdateActionResult,
  onUpdateActionReroll,
  onRemove,
  className,
}: ActionGroupProps) {
  const { t } = useLanguage();
  const playerList = Object.values(players);
  const selectedPlayer = group.playerId ? (players[group.playerId] ?? null) : null;

  return (
    <div
      className={cn(
        "border border-bb-gray-mid mb-4 bg-black/30",
        className
      )}
    >
      {/* Group Header */}
      <div
        className={cn(
          "flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2",
          "p-3 bg-bb-gray-mid border-b border-bb-gray-mid"
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-bb-bone-white text-sm">{t.player.replace(" #", ":")}</span>
          <select
            value={group.playerId || ""}
            onChange={(e) => onSetPlayer(e.target.value || null)}
            className={cn(
              "px-3 py-1.5 text-sm",
              "bg-bb-gray-dark border border-bb-gray-mid rounded",
              "text-bb-bone-white cursor-pointer",
              "hover:border-bb-blood-red",
              "focus:border-bb-gold focus:outline-none"
            )}
          >
            <option value="">{t.noPlayer}</option>
            {playerList.map((player) => {
              const skillsLabel = getPlayerSkillsLabel(player, t.skills);
              return (
                <option key={player.id} value={player.id}>
                  #{player.number}{skillsLabel ? ` (${skillsLabel})` : ""}
                </option>
              );
            })}
          </select>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            "font-condensed text-sm font-semibold uppercase tracking-wide",
            "px-3 py-1.5 transition-all",
            "bg-transparent text-bb-blood-bright border border-bb-blood-red",
            "hover:bg-bb-blood-red hover:text-bb-bone-white",
            "sm:self-auto self-end -mt-8 sm:mt-0"
          )}
          aria-label={t.removeActionGroup}
        >
          ✕
        </button>
      </div>

      {/* Actions List */}
      <div className="p-4">
        {group.actions.length === 0 ? (
          <p className="text-bb-bone-white/60 text-center py-2">
            {t.noActions}
          </p>
        ) : (
          group.actions.map((action) => (
            <ActionItem
              key={action.id}
              action={action}
              player={selectedPlayer}
              onUpdateType={(type) => onUpdateActionType(action.id, type)}
              onUpdateResult={(result) => onUpdateActionResult(action.id, result)}
              onUpdateReroll={(reroll) => onUpdateActionReroll(action.id, reroll)}
              onRemove={() => onRemoveAction(action.id)}
            />
          ))
        )}

        <button
          type="button"
          onClick={onAddAction}
          className={cn(
            "w-full mt-2 font-condensed text-sm font-semibold uppercase tracking-wide",
            "px-3 py-2 transition-all",
            "bg-bb-pitch-green text-bb-bone-white border-2 border-bb-pitch-dark",
            "hover:bg-bb-pitch-dark hover:border-bb-gold"
          )}
        >
          {t.addAction}
        </button>
      </div>
    </div>
  );
}
